import { useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { t } from '../i18n';
import './CameraCapture.css';

/** Whether this browser can show the camera inside the page (Safari on iPhone can, over HTTPS). */
export function canUseCamera(): boolean {
  return typeof navigator !== 'undefined' && !!navigator.mediaDevices?.getUserMedia;
}

/**
 * The bottle scan's camera (owner request, 2026-10-14): one tap opens the back camera inside the
 * app, the first photo is the front of the bottle and the second the back, then it closes by
 * itself. "Sans le dos" ends after the front. If the camera cannot be opened (refused, none),
 * `onUnavailable` lets the page fall back to the phone's own camera, one photo at a time.
 */
export function CameraCapture({
  onDone,
  onCancel,
  onUnavailable,
}: {
  /** The photos taken: the front, then the back if there is one. */
  onDone: (photos: Blob[]) => void;
  onCancel: () => void;
  onUnavailable: () => void;
}) {
  const video = useRef<HTMLVideoElement>(null);
  const [ready, setReady] = useState(false);
  const [busy, setBusy] = useState(false);
  const [front, setFront] = useState<{ photo: Blob; preview: string } | undefined>();

  useEffect(() => {
    let stream: MediaStream | undefined;
    let stopped = false;
    navigator.mediaDevices
      .getUserMedia({
        audio: false,
        video: { facingMode: 'environment', width: { ideal: 1920 }, height: { ideal: 1440 } },
      })
      .then(async (opened) => {
        stream = opened;
        if (stopped || !video.current) return stopAll(opened);
        video.current.srcObject = opened;
        await video.current.play();
        if (!stopped) setReady(true);
      })
      .catch(() => {
        if (!stopped) onUnavailable();
      });
    return () => {
      stopped = true;
      if (stream) stopAll(stream);
    };
    // Opened once, when the camera is shown.
  }, []);

  useEffect(() => () => (front ? URL.revokeObjectURL(front.preview) : undefined), [front]);

  async function shoot() {
    const frame = video.current;
    if (!frame || !frame.videoWidth) return;
    setBusy(true);
    try {
      const canvas = document.createElement('canvas');
      canvas.width = frame.videoWidth;
      canvas.height = frame.videoHeight;
      canvas.getContext('2d')!.drawImage(frame, 0, 0);
      const photo = await new Promise<Blob | null>((resolve) =>
        canvas.toBlob(resolve, 'image/jpeg', 0.92),
      );
      if (!photo) return;
      if (front) onDone([front.photo, photo]);
      else setFront({ photo, preview: URL.createObjectURL(photo) });
    } finally {
      setBusy(false);
    }
  }

  return createPortal(
    <div className="camera" role="dialog" aria-modal="true" aria-label={t('scan.action')}>
      <video ref={video} className="camera__video" playsInline muted autoPlay />
      <p className="camera__step" aria-live="polite">
        {front ? t('camera.stepBack') : t('camera.stepFront')}
      </p>
      <div className="camera__bar">
        <button type="button" onClick={onCancel}>
          {t('common.cancel')}
        </button>
        <button
          type="button"
          className="cta-action camera__shoot"
          disabled={!ready || busy}
          onClick={() => void shoot()}
        >
          {front ? t('camera.takeBack') : t('camera.takeFront')}
        </button>
        {front ? (
          <button type="button" className="camera__skip" onClick={() => onDone([front.photo])}>
            <img src={front.preview} alt={t('scan.frontPhoto')} className="camera__thumb" />
            {t('camera.skipBack')}
          </button>
        ) : (
          <span />
        )}
      </div>
    </div>,
    document.body,
  );
}

function stopAll(stream: MediaStream) {
  for (const track of stream.getTracks()) track.stop();
}
